import 'normalize.css'
import { createPinia } from 'pinia'
import { createApp } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'

import { initialise as initialiseConfig } from '../config'
import { terminate } from '../interview/apis/terminate'
import {
  DEFAULT_COUNTRY,
  DEFAULT_LANGUAGE,
  isRtlLocale,
  setLocale,
  transformLanguageToTranslationKey,
} from '../intl'
import marketeerRoutes from '../marketeer-legacy/routes'

import App from './components/pages/App.vue'
import routes from './routes'
import { capture, setGlobalContext, setupErrorTracking } from './setup'
import './styles/button.postcss'
import './styles/main.postcss'
import './styles/select.postcss'

;(async function main() {
  try {
    await initialiseConfig()
    const app = createApp(App)
    setupErrorTracking(app)
    setGlobalContext()

    const searchParams = new URLSearchParams(window.location.search)
    const language = searchParams.get('language')?.toUpperCase() ?? DEFAULT_LANGUAGE
    const country = searchParams.get('country_of_residence_v2')?.toUpperCase() ?? DEFAULT_COUNTRY
    await setLocale(transformLanguageToTranslationKey(language, country))
    if (isRtlLocale()) {
      window.document.body.dir = 'rtl'
    }

    const router = createRouter({
      history: createWebHistory(import.meta.env.BASE_URL),
      routes: [
        {
          path: '/app',
          component: () => import('./components/pages/UnresponsiveContainerPage.vue'),
          children: routes,
        },
        { path: '/preview', component: () => import('./components/pages/PreviewPage.vue') },
        { path: '/marketeer', children: marketeerRoutes },
        {
          path: '/:pathMatch(.*)*',
          component: () => import('./components/pages/PageNotFound.vue'),
        },
      ],
    })

    const pinia = createPinia()

    app.use(router)
    app.use(pinia)
    app.mount('#app')
    manageDocumentVisibilityChange()
  } catch (e) {
    capture(e as Error)
    term()
  }
})()

async function term(): Promise<void> {
  try {
    const roundGuid = window.location.pathname.match(/\/?app\/(?<guid>[a-zA-Z0-9-]+)/)?.groups?.guid
    if (roundGuid) {
      await terminate({ roundGuid, reason: 'term' })
    }
  } catch (e) {
    capture(e as Error)
  }
}

function manageDocumentVisibilityChange(): void {
  if (window.self !== window.top) {
    // If the window is in an iframe, we don't want to handle visibility changes
    // as this can cause issues if the user wrapping parent has ui that they could potentially interact with
    // which would put us out of visibility
    return
  }

  // track page visibility changes to provide metrics on how often people are leaving the page
  // when returning to visible state we reload the page to ensure the latest state is reflected
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      location.reload()
      return
    }
  })
}
